/**
 * Shared content for the redesigned service pages.
 * Each entry carries bilingual fields and the brand-color hex used by
 * the service detail page. Sourced from research on each product site.
 */

export const SERVICE_CONTENT = {
  japanetccard: {
    accent: "#5B2E83",
    accentSoft: "#F4EEF9",
    accentDeep: "#3F1F5B",
    domain: "JapanETCcard.com",
    href: "https://japanetccard.com",
    established: "2013",
    customers: "Established customer base",
    eyebrow: { ja: "運営サービス / 01", en: "Operated Service / 01" },
    title: { ja: "JapanETCcard.com", en: "JapanETCcard.com" },
    headline: {
      ja: "日本で暮らす外国人の方に向けた、ETCカードサポートサービス。",
      en: "An ETC card service built for foreign residents in Japan.",
    },
    intro: {
      ja: "JapanETCcard.comは、日本の高速道路でETCを利用したい外国人居住者、米軍関係者とそのご家族、大使館関係者、在日企業のためのETCカードサポートサービスです。2013年の創業以来、日本全国のお客様に日本語・英語でサポートを提供しています。",
      en: "JapanETCcard.com is an independent ETC card support service for foreign residents, U.S. military families, embassy staff, and international companies driving in Japan. Operating since 2013, we have supported a focused customer base across Japan in both English and Japanese.",
    },
    stats: [
      { valueJa: "EST. 2013", valueEn: "EST. 2013", labelJa: "創業", labelEn: "Established" },
      { valueJa: "JA / EN", valueEn: "JA / EN", labelJa: "対応言語", labelEn: "Support languages" },
      { value: "24/7", labelJa: "ダッシュボード", labelEn: "Account dashboard" },
      { valueJa: "60日間", valueEn: "60-day", labelJa: "無料トライアル", labelEn: "Free trial" },
    ],
    features: [
      {
        titleJa: "英語対応のETC専門サポート",
        titleEn: "English-speaking ETC specialists",
        bodyJa: "日本語・英語の両方に対応したETC専門スタッフが、登録から請求までのご質問に対応します。",
        bodyEn: "Bilingual English and Japanese ETC specialists handle questions across signup, billing and toll usage.",
      },
      {
        titleJa: "派遣・展開中の方にも安心",
        titleEn: "Ideal for deployed personnel",
        bodyJa: "ETCカードを使用しない月は請求が発生しません。日本を離れている期間も安心してご利用いただけます。",
        bodyEn: "No charges are billed during months when the card is unused — ideal when you are deployed or away from Japan.",
      },
      {
        titleJa: "月次請求・年間ETC利用レポート",
        titleEn: "Monthly invoice & yearly tax report",
        bodyJa: "前月分のETC利用記録をまとめた月次レポートと、年末の確認に役立つETC利用レポートを自動的に作成します。",
        bodyEn: "Consolidated monthly toll invoices and an end-of-year ETC tax report are generated for you automatically.",
      },
      {
        titleJa: "24時間アクセスのダッシュボード",
        titleEn: "24/7 billing dashboard",
        bodyJa: "ご自身のステータス、ETC利用履歴、請求情報をいつでも確認できる専用ダッシュボードを提供します。",
        bodyEn: "A 24/7 customer dashboard shows your status, ETC usage, invoices and account information whenever you need it.",
      },
      {
        titleJa: "ETC割引・マイレージ対応",
        titleEn: "ETC discounts & commuter mileage",
        bodyJa: "時間・距離・休日割引、新2030 ETCカードによるマイレージ還元など、ETC利用ならではのお得な仕組みに対応。",
        bodyEn: "Time, distance and holiday discounts plus commuter mileage credits via the new 2030 ETC card are reflected in your account.",
      },
      {
        titleJa: "ETC利用明細・出張精算用レポート",
        titleEn: "Travel claim receipts on demand",
        bodyJa: "出張精算や経費処理に必要な期間を指定して、PDF形式のETC利用明細・利用証明書をリクエストいただけます。",
        bodyEn: "Request PDF toll receipts for any date range — useful for business travel claims and expense reporting.",
      },
    ],
    audiences: [
      { ja: "米軍・SOFAステータスの方", en: "U.S. military & SOFA-status families" },
      { ja: "大使館・領事館の方", en: "Embassies and consulates" },
      { ja: "在日企業・コントラクター", en: "International companies & contractors" },
      { ja: "教員・宣教師・NGO", en: "Teachers, missionaries, NGO staff" },
      { ja: "退役・退職後も日本在住の方", en: "Retired personnel still living in Japan" },
      { ja: "MOFA・MOC関係者", en: "MOFA / MOC related residents" },
    ],
    fees: undefined,
    featureLinks: [
      {
        titleJa: "サービスの仕組み",
        titleEn: "How it works",
        summaryJa: "申込から発行、利用開始までの流れを画像付きで解説。",
        summaryEn: "Step-by-step explanation of application, issuance and activation — with visuals.",
        href: "https://japancardetc.com/how-it-works",
      },
      {
        titleJa: "ETC Toll Tracker",
        titleEn: "ETC Toll Tracker",
        summaryJa: "全ETCカードの通行履歴・請求情報・残高をひとつのダッシュボードで確認。",
        summaryEn: "All ETC card trips, billing and balances in a single account dashboard.",
        href: "https://japancardetc.com/receipts-reports",
      },
      {
        titleJa: "Toll Explorer",
        titleEn: "Toll Explorer",
        summaryJa: "高速道路の出口・料金所・通行ルートをマップで検索・比較。",
        summaryEn: "Search and compare expressway exits, gates and routes on an interactive map.",
        href: "https://japancardetc.com/toll-explorer",
      },
      {
        titleJa: "Toll Guides",
        titleEn: "Toll Guides",
        summaryJa: "ETC・通行ルール・支払い方法に関するバイリンガルガイド。",
        summaryEn: "Bilingual guides covering ETC, toll rules and payment methods.",
        href: "https://japancardetc.com/toll-guides",
      },
      {
        titleJa: "登録サポート",
        titleEn: "Registration support",
        summaryJa: "申請書類・本人確認・在留資格に応じた個別サポート。",
        summaryEn: "Hands-on support for application paperwork, ID checks and residency-based eligibility.",
        href: "https://japancardetc.com/shipping-instructions",
      },
      {
        titleJa: "60日間無料トライアル",
        titleEn: "60-Day Free Trial",
        summaryJa: "60日間無料でJapanETCcardをお試しいただけます。日英対応のサポート付き。",
        summaryEn: "Try JapanETCcard free for 60 days — with bilingual JA/EN support included.",
        href: "https://japancardetc.com/free-trial",
      },
    ],
    planLinks: [
      {
        nameJa: "料金プラン",
        nameEn: "Pricing",
        summaryJa: "初期費用・デポジット・月額の最新の料金体系は、JapanETCcardの公式サイトに常時掲載しています。",
        summaryEn: "Setup, deposit and monthly pricing details are kept current on the JapanETCcard site.",
        href: "https://japancardetc.com/pricing",
      },
      {
        nameJa: "発行スケジュール",
        nameEn: "Issuance Dates",
        summaryJa: "申請からカード発送までのスケジュール・所要日数を公開しています。",
        summaryEn: "Application-to-shipping timelines and processing dates — published in advance.",
        href: "https://japancardetc.com/issuance-dates",
      },
      {
        nameJa: "リファラル特典",
        nameEn: "Refer & Earn ¥3,000",
        summaryJa: "ご紹介いただいたお客様がご契約されると、¥3,000のリワードを進呈しています。",
        summaryEn: "Refer a new customer and receive ¥3,000 once their account is active.",
        href: "https://japancardetc.com/refer-a-friend",
      },
    ],
    note: {
      ja: "JapanETCcard.comは独立したサービスであり、NEXCO・ETC利用照会サービスや高速道路会社の公式サービスではありません。",
      en: "JapanETCcard.com is an independent service and is not affiliated with NEXCO, the ETC Usage Inquiry Service, or any toll-road operator's official service.",
    },
    support: {
      email: "support@japanetccard.com",
      hoursJa: "平日 9:00 – 18:00 JST (日本語・英語)",
      hoursEn: "Mon – Fri · 09:00 – 18:00 JST (Japanese / English)",
    },
    // Short factual FAQ. Used both for the visible disclosure list on the
    // service detail page and for FAQPage schema.org markup. Keep answers
    // under ~280 chars each for clean SERP rendering.
    faqs: [
      {
        q: {
          en: "Who is JapanETCcard for?",
          ja: "JapanETCcardはどんな方が対象ですか？",
        },
        a: {
          en: "JapanETCcard supports foreign residents in Japan, U.S. military families, embassy staff, and international companies whose drivers use the expressway network. Both English and Japanese are first-class support languages.",
          ja: "Question JA: JapanETCcardはどんな方が対象ですか？",
        },
      },
      {
        q: {
          en: "Is JapanETCcard an official NEXCO card?",
          ja: "JapanETCcardはNEXCO公式のサービスですか？",
        },
        a: {
          en: "No. JapanETCcard.com is an independent ETC card support service. It is not affiliated with NEXCO, the ETC Usage Inquiry Service (MEISAI), or any toll-road operator's official issuance program.",
          ja: "いいえ。JapanETCcard.comは独立したETCカードサポートサービスです。NEXCO、ETC利用照会サービス（MEISAI）、その他の高速道路事業者の公式発行プログラムとは提携していません。",
        },
      },
      {
        q: {
          en: "How long has JapanETCcard been operating?",
          ja: "サービスはいつから運営していますか？",
        },
        a: {
          en: "Since 2013. We started in Yokosuka, Kanagawa, and have grown a focused customer base across Japan in both English and Japanese over the last 13+ years.",
          ja: "Question JA: サービスはいつから運営していますか？",
        },
      },
      {
        q: {
          en: "What support languages does JapanETCcard offer?",
          ja: "対応言語は何ですか？",
        },
        a: {
          en: "All customer-facing communications — signup, dashboard, billing notes and email support — are available in both Japanese and English from the same trained team.",
          ja: "申込み、ダッシュボード、請求関連の案内、メールサポートまで、お客様向けコミュニケーションは日本語・英語の両方に対応しています。同じサポートチームが、ご希望の言語に合わせて対応します。",
        },
      },
      {
        q: {
          en: "Is there a free trial?",
          ja: "無料トライアルはありますか？",
        },
        a: {
          en: "Yes. New accounts include a 60-day free trial of the account dashboard so customers can verify the experience before committing.",
          ja: "Question JA: 無料トライアルはありますか？",
        },
      },
    ],
  },

  japantollreceipts: {
    accent: "#2F8F3C",
    accentSoft: "#ECF6EC",
    accentDeep: "#226A2C",
    domain: "JapanTollReceipts.jp",
    href: "https://japantollreceipts.jp",
    established: "2013",
    customers: "Established customer base",
    eyebrow: { ja: "運営サービス / 02", en: "Operated Service / 02" },
    title: { ja: "JapanTollReceipts.jp", en: "JapanTollReceipts.jp" },
    headline: {
      ja: "ETC利用記録を、毎朝のメールに整理。",
      en: "Your ETC toll records, organized in your inbox every morning.",
    },
    intro: {
      ja: "JapanTollReceipts.jp（JTR）は、ETC利用明細・ETC利用証明書を、日次・週次・月次でメールに自動配信するレポートサービスです。個人ドライバー、法人、車両管理者、官公庁のお客様のMEISAI確認作業の負担を軽減します。",
      en: "JapanTollReceipts.jp (JTR) automatically delivers ETC usage records and toll certificates to your inbox on a daily, weekly or monthly schedule — so individual drivers, businesses, fleet managers and government users no longer have to log into MEISAI by hand each week.",
    },
    stats: [
      { value: "99.97%", labelJa: "配信稼働率", labelEn: "Delivery uptime" },
      { valueJa: "31項目", valueEn: "31 checks", labelJa: "送信前検証", labelEn: "Pre-send validation checks" },
      { value: "PDF + CSV", labelJa: "出力形式", labelEn: "Report formats" },
      { value: "JA / EN", labelJa: "対応言語", labelEn: "Email languages" },
    ],
    features: [
      {
        titleJa: "日次ETCレポート",
        titleEn: "Daily ETC reports",
        bodyJa: "毎朝 JST 10:00 までに、前日の通行料金をまとめた PDF と CSV が受信箱に届きます。毎回MEISAIへ手動ログインする必要はありません。",
        bodyEn: "Each morning by 10:00 JST, a PDF and CSV summary of the previous day's tolls lands in your inbox. No MEISAI login required.",
      },
      {
        titleJa: "スマート支出アラート",
        titleEn: "Smart spending alerts",
        bodyJa: "カード別のしきい値を設定。日次・週次・月次の通行料が一定額を超えると即時に通知します。",
        bodyEn: "Set per-card thresholds. Get notified the moment daily, weekly or monthly toll spend crosses a configured line.",
      },
      {
        titleJa: "立替精算書類",
        titleEn: "Reimbursement-ready reports",
        bodyJa: "日付・ルート・入口/出口IC・プレート照合・金額を1行ずつ整理した、提出用に確認しやすいPDFを生成します。",
        bodyEn: "Submission-ready PDFs with date, route, entry/exit IC, plate matching and amount lined up row by row.",
      },
      {
        titleJa: "認可車両チェック",
        titleEn: "Authorized vehicle registry",
        bodyJa: "承認済み車両リストを作成。リスト外の利用は人によるレビューに回し、自動停止はしません。",
        bodyEn: "Whitelist your fleet. Unlisted usage is flagged for human review — never automatically blocked.",
      },
      {
        titleJa: "MEISAI自動化",
        titleEn: "MEISAI automation",
        bodyJa: "JTR が ETC 利用情報を取得・整形・配信。サイレント再認証と接続状態の自動監視を内蔵しています。",
        bodyEn: "JTR pulls, formats and ships your ETC usage automatically — including silent re-authentication and connection health monitoring.",
      },
      {
        titleJa: "車両利用の可視化",
        titleEn: "Vehicle usage review",
        bodyJa: "時間外利用・反復ルート・新規組合せなどの異常を人によるレビューのために提示します。最終判断は常に人が行います。",
        bodyEn: "Off-hours use, repeat routes and new combinations are surfaced for human review. Final judgment always stays with a person.",
      },
    ],
    audiences: [
      { ja: "個人ドライバー", en: "Individual drivers" },
      { ja: "法人・車両管理者", en: "Businesses & fleet managers" },
      { ja: "官公庁・自衛隊・警察・消防", en: "Government, SDF, police, fire" },
      { ja: "配送・運送業者", en: "Logistics & delivery" },
      { ja: "個人事業主・出張の多い方", en: "Sole proprietors & frequent travelers" },
      { ja: "米軍・SOFA関係者", en: "U.S. military & SOFA users" },
    ],
    plans: undefined,
    featureLinks: [
      {
        titleEn: "Daily ETC Reports",
        titleJa: "日次ETCレポート",
        summaryEn: "Yesterday's ETC activity in your inbox every morning. No MEISAI logins, no scraping, no portal hopping. PDF + CSV, bilingual.",
        summaryJa: "前日のETC利用記録を毎朝メールでお届けします。MEISAIへの手作業ログインやポータル確認の手間を減らし、PDF・CSV形式で日本語・英語に対応します。",
        tags: ["Personal", "Business", "Government", "Reports"],
        href: "https://japantollreceipts.jp/features/daily-etc-reports",
      },
      {
        titleEn: "Smart Spend Alerts",
        titleJa: "スマート支出アラート",
        summaryEn: "Define rules once. JTR pings you the moment ETC activity drifts outside expected patterns — thresholds, weekends, after-hours, dormant cards.",
        summaryJa: "ルールを一度設定すれば、想定外のパターン（しきい値超過・週末・時間外・休眠カードなど）を確認候補としてJTRが通知します。",
        tags: ["Personal", "Business", "Government", "Alerts"],
        href: "https://japantollreceipts.jp/features/smart-alerts",
      },
      {
        titleEn: "Authorized Vehicle Registry",
        titleJa: "認可車両レジストリ",
        summaryEn: "Allow-list every approved vehicle — company fleet, family cars, official motor pool. JTR flags ETC activity on plates outside the list.",
        summaryJa: "社用車・自家用車・官公庁の公用車など、承認済み車両を許可リスト化。リスト外のナンバープレートでETCが使用された可能性がある場合に確認候補として表示します。",
        tags: ["Personal", "Business", "Government", "Authorized Vehicles"],
        href: "https://japantollreceipts.jp/features/authorized-vehicle-registry",
      },
      {
        titleEn: "Reimbursement Reports",
        titleJa: "経費精算レポート",
        summaryEn: "Accountant-ready travel claims with toll-by-toll breakdowns and receipt-grade PDFs. Generate a submission package for any date range in one click.",
        summaryJa: "経理担当者が確認しやすい出張精算用レポート。通行ごとの内訳と確認用PDFを、任意の期間で1クリック生成します。",
        tags: ["Personal", "Business", "Government", "Reimbursement"],
        href: "https://japantollreceipts.jp/features/reimbursement-reports",
      },
      {
        titleEn: "MEISAI Automation",
        titleJa: "MEISAI 自動連携",
        summaryEn: "Pass-through architecture pulls MEISAI data once a day, formats it, delivers it, and forgets it. Your toll history never persists on our servers.",
        summaryJa: "パススルー型の設計により、MEISAIデータを1日1回取得・整形・配信し、不要になったデータは保存しません。通行履歴を当社サーバーに長期保存しない方針です。",
        tags: ["Personal", "Business", "Government", "Security"],
        href: "https://japantollreceipts.jp/features/meisai-automation",
      },
      {
        titleEn: "Pass-Through Security",
        titleJa: "パススルー型セキュリティ",
        summaryEn: "Zero-retention pipeline: MEISAI rows flow in, get processed, get delivered, and get wiped. No permanent toll history on JTR servers.",
        summaryJa: "保存を最小限に抑える処理設計。MEISAIデータを取得・処理・配信した後、不要になったデータは削除し、JTRサーバーに通行履歴を長期保存しない方針です。",
        tags: ["Personal", "Business", "Government", "Security"],
        href: "https://japantollreceipts.jp/security",
      },
      {
        titleEn: "PDF / CSV Exports",
        titleJa: "PDF / CSV エクスポート",
        summaryEn: "One-click downloads in the formats your accountant, tax filer, or audit team already uses. PDF for submission, CSV for reconciliation.",
        summaryJa: "経理・確認作業で扱いやすい形式を、ワンクリックでダウンロードできます。確認用のPDF、突合せ用のCSVに対応します。",
        tags: ["Personal", "Business", "Government", "Reports"],
        href: "https://japantollreceipts.jp/sample-outputs",
      },
      {
        titleEn: "Bulk Reports",
        titleJa: "一括レポート",
        summaryEn: "Consolidate dozens of MEISAI accounts and ETC cards into one branch-by-branch monthly roll-up. Built for multi-site fleets and motor pools.",
        summaryJa: "複数のMEISAIアカウントとETCカードを、拠点ごとの月次レポートに集約。多拠点のフリート運用や官公庁の車両管理向けです。",
        tags: ["Business", "Government", "Reports"],
        href: "https://japantollreceipts.jp/business-reports",
      },
      {
        titleEn: "Bilingual Reports",
        titleJa: "日英対応レポート",
        summaryEn: "Same data, EN + JA columns side-by-side. Built for international finance teams, joint programs, and global audit reviews.",
        summaryJa: "同じデータを、日本語・英語の列で並べて確認できます。国際的な経理チーム、共同プロジェクト、海外拠点による確認にも使いやすい構成です。",
        tags: ["Business", "Government", "Reports"],
        href: "https://japantollreceipts.jp/features",
      },
      {
        titleEn: "Plate Type Awareness",
        titleJa: "ナンバープレート種別認識",
        summaryEn: "MEISAI exposes only the last 4 of each plate. JTR lets you register the full plate identifier (Y / A / E / 外 / JSDF / police / fire / public) so every last-4 hit is mapped to the most likely vehicle — never auto-confirmed.",
        summaryJa: "MEISAIで確認できるナンバー情報には制限があります。JTRでは、登録された車両識別情報をもとに、下4桁が一致した利用記録を候補車両に関連付けます（自動確定はしません）。",
        tags: ["Government", "Authorized Vehicles"],
        href: "https://japantollreceipts.jp/government/plate-types",
      },
      {
        titleEn: "Per-Card Email Routing",
        titleJa: "カード別メール振り分け",
        summaryEn: "Send each ETC card's statement to a different recipient — driver, branch manager, accountant. No more 'where do I forward this' chains.",
        summaryJa: "ETCカードごとに、ドライバー、支店長、経理担当者など別々の宛先へ明細レポートを送付できます。転送作業の手間を減らせます。",
        tags: ["Business", "Government", "Alerts"],
        href: "https://japantollreceipts.jp/features",
      },
      {
        titleEn: "Manager Review Queue",
        titleJa: "管理者確認キュー",
        summaryEn: "Borderline ETC activity routes to a queue for 'approve / needs review' — never auto-flagged as misuse. Final decision stays with a human.",
        summaryJa: "境界線にあるETC利用は「承認 / 要確認」のキューに送られます。不正利用と断定することはなく、最終判断は人が行います。",
        tags: ["Business", "Government", "Authorized Vehicles"],
        href: "https://japantollreceipts.jp/business-suite",
      },
      {
        titleEn: "Audit-Ready Reports",
        titleJa: "内部確認に使いやすいレポート",
        summaryEn: "Tamper-evident PDFs with verification stamps and chain-of-custody metadata that satisfy internal-audit and procurement officers.",
        summaryJa: "確認用PDF、検証用スタンプ、処理履歴メタデータにより、内部確認や調達部門での確認作業を支援します。",
        tags: ["Business", "Government", "Reports"],
        href: "https://japantollreceipts.jp/features/reimbursement-reports",
      },
      {
        titleEn: "AI Pattern Review",
        titleJa: "AI パターンレビュー",
        summaryEn: "Our AI surfaces 'possible' / 'likely' anomalies — weekend trips, recurring unknown plates, after-hours patterns. AI assists, never auto-judges.",
        summaryJa: "AIが、週末利用、繰り返し現れる未登録ナンバー、時間外利用などの確認候補を提示します。AIは判断を確定せず、最終確認は人が行います。",
        tags: ["Business", "Government", "AI"],
        href: "https://japantollreceipts.jp/features/meisai-automation",
      },
      {
        titleEn: "Travel Claims",
        titleJa: "出張精算",
        summaryEn: "One-click receipt-grade claim package for any date range, ready for company submission. Built for sales reps, consultants, and one-off projects.",
        summaryJa: "任意の期間で、出張精算に使いやすい確認用パッケージを1クリック生成。営業担当、コンサルタント、単発プロジェクトの精算業務を支援します。",
        tags: ["Personal", "Business", "Reimbursement"],
        href: "https://japantollreceipts.jp/features/reimbursement-reports",
      },
      {
        titleEn: "Weekly ETC Reports",
        titleJa: "週次ETCレポート",
        summaryEn: "Same daily engine, weekly digest. For teams that don't need the daily firehose — Monday-morning roll-up of the prior week's activity.",
        summaryJa: "日次レポートと同じエンジンの週次ダイジェスト。毎日の詳細が不要なチーム向けに、月曜朝に前週の利用をまとめて配信します。",
        tags: ["Personal", "Business", "Reports"],
        href: "https://japantollreceipts.jp/free-weekly-reports",
      },
      {
        titleEn: "Monthly ETC Reports",
        titleJa: "月次ETCレポート",
        summaryEn: "Calendar-month statement aligned to credit-card billing cycles. Month-close reconciliation, audit cycles, and billing dispute resolution all get easier.",
        summaryJa: "クレジットカードの請求サイクルに合わせた月次明細レポート。月次締め、確認作業、請求差異の確認が進めやすくなります。",
        tags: ["Personal", "Business", "Government", "Reports"],
        href: "https://japantollreceipts.jp/features",
      },
      {
        titleEn: "Custom Date-Range Reports",
        titleJa: "期間指定レポート",
        summaryEn: "Pick any window. Useful for trip-by-trip reimbursement, multi-week project billing, or short business-stay reconciliation.",
        summaryJa: "任意の期間を指定。出張ごとの精算、数週間のプロジェクト請求、短期滞在の経費突合せなどに便利です。",
        tags: ["Personal", "Business", "Government", "Reports"],
        href: "https://japantollreceipts.jp/features",
      },
      {
        titleEn: "MEISAI Connection Health",
        titleJa: "MEISAI 接続状態",
        summaryEn: "Daily heartbeat from every MEISAI account. JTR alerts before a missed-report becomes an audit gap or month-close surprise.",
        summaryJa: "各MEISAIアカウントの接続状況を日次で確認。レポートの未取得や月次確認の漏れにつながる前に通知します。",
        tags: ["Business", "Government", "Security"],
        href: "https://japantollreceipts.jp/features/meisai-automation",
      },
      {
        titleEn: "Unauthorized Vehicle Alerts",
        titleJa: "未承認車両アラート",
        summaryEn: "ETC card used on a plate that's not on the authorized list? You hear about it by morning. Early-warning only — the card is never blocked.",
        summaryJa: "承認済み車両リストにない車両でETCカードが使われた可能性がある場合、翌朝に通知。早期警告のみで、カードを自動でブロックすることはありません。",
        tags: ["Business", "Government", "Alerts"],
        href: "https://japantollreceipts.jp/features/authorized-vehicle-monitoring",
      },
      {
        titleEn: "Family Vehicle List",
        titleJa: "家族の車両リスト",
        summaryEn: "A short, private list of the vehicles in your household so every ETC trip gets labelled with the right vehicle in your daily / weekly / monthly receipt.",
        summaryJa: "ご家庭の車両を短い非公開リストに登録すれば、日次・週次・月次レポートで各通行を車両候補として整理しやすくなります。",
        tags: ["Personal", "Authorized Vehicles"],
        href: "https://japantollreceipts.jp/personal/family-vehicle-list",
      },
      {
        titleEn: "Authorized Vehicle Monitoring & Misuse Detection",
        titleJa: "承認済み車両モニタリングと利用状況チェック",
        summaryEn: "Daily comparison of each ETC card's activity against your authorized vehicle list. Possible misuse is flagged for human review — never auto-blocked, never 'confirmed fraud'.",
        summaryJa: "各ETCカードの利用を、承認済み車両リストと毎日突合せます。未登録車両での利用可能性がある場合は、人による確認に回します。自動ブロックや「不正確定」は行いません。",
        tags: ["Business", "Government", "Alerts"],
        href: "https://japantollreceipts.jp/features/authorized-vehicle-monitoring",
      },
    ],
    planLinks: [
      {
        nameJa: "Free Weekly",
        nameEn: "Free Weekly",
        summaryJa: "週次の自動レポート。最大10枚のETCカードに対応。詳細とお申込みはJapanTollReceipts公式サイトでご確認ください。",
        summaryEn: "Free weekly auto-reports for up to 10 ETC cards. See current details and sign up on the JapanTollReceipts site.",
        href: "https://japantollreceipts.jp/free-weekly-reports",
      },
      {
        nameJa: "Daily Plus (Premium)",
        nameEn: "Daily Plus (Premium)",
        summaryJa: "日次・週次・月次レポートのプレミアムプラン。現在の料金はJapanTollReceipts公式サイトでご確認ください。",
        summaryEn: "Daily, weekly and monthly premium reports. Current pricing is maintained on the JapanTollReceipts site.",
        href: "https://japantollreceipts.jp/daily-reports",
      },
      {
        nameJa: "ビジネスプラン",
        nameEn: "Business plan",
        summaryJa: "複数ドライバー・複数部門の経費フローに対応する法人プラン。",
        summaryEn: "For organizations with multiple drivers and departmental expense flows.",
        href: "https://japantollreceipts.jp/business",
      },
      {
        nameJa: "官公庁・防衛プラン",
        nameEn: "Government & Defense plan",
        summaryJa: "官公庁・自衛隊・米軍向けの専用プラン。請求書発行・大量導入に対応。",
        summaryEn: "Dedicated plan for government agencies, SDF and U.S. military — invoicing and bulk onboarding supported.",
        href: "https://japantollreceipts.jp/government",
      },
      {
        nameJa: "1年プレミアム無料体験",
        nameEn: "1-Year Premium Free Trial",
        summaryJa: "先着の個人・法人・官公庁のお客様に、1年間のプレミアム機能を無料でご提供。クレジットカード登録は不要です。",
        summaryEn: "First-served personal, business and government users get a full year of premium features — no credit card required.",
        href: "https://japantollreceipts.jp/premium-reports",
      },
    ],
    note: {
      ja: "JapanTollReceipts.jpは独立したサービスです。NEXCO、道路事業者、ETC利用照会サービスの公式運営ではありません。また、当社が通行料金を請求したり、未払い料金を回収したりするサービスではありません。",
      en: "JapanTollReceipts.jp is an independent service. It is not NEXCO, a toll-road operator, or the official ETC Usage Inquiry Service. We never bill tolls or collect unpaid fees.",
    },
    support: {
      email: "support@japantollreceipts.jp",
      hoursJa: "平日 9:00 – 18:00 JST (日本語・英語)",
      hoursEn: "Mon – Fri · 09:00 – 18:00 JST (Japanese / English)",
    },
    faqs: [
      {
        q: {
          en: "What does JapanTollReceipts do?",
          ja: "JapanTollReceiptsはどのようなサービスですか？",
        },
        a: {
          en: "JapanTollReceipts automatically retrieves ETC toll usage records, runs a pre-send validation pipeline of 31 checks, and emails clean PDF + CSV reports to drivers, fleet managers and back offices on a daily, weekly or monthly schedule.",
          ja: "Question JA: JapanTollReceiptsはどのようなサービスですか？",
        },
      },
      {
        q: {
          en: "Is JapanTollReceipts the same as MEISAI?",
          ja: "MEISAI（ETC利用照会サービス）と同じサービスですか？",
        },
        a: {
          en: "No. JapanTollReceipts.jp is an independent service that works around the official ETC Usage Inquiry Service (MEISAI). We do not bill tolls or collect unpaid fees — we deliver pre-validated reports of your own ETC usage to your inbox.",
          ja: "いいえ。JapanTollReceipts.jpは、公式のETC利用照会サービス（MEISAI）の利用明細確認を補助する独立したサービスです。当社が通行料金を請求したり、未払い料金を回収したりするサービスではありません。お客様ご自身のETC利用明細を、事前チェック済みのレポートとしてメールでお届けします。",
        },
      },
      {
        q: {
          en: "What report formats are available?",
          ja: "レポートの形式は何ですか？",
        },
        a: {
          en: "Every scheduled delivery includes both a human-readable PDF and a machine-readable CSV — so finance teams can drop the file directly into accounting, expense or fleet-management systems without rekeying.",
          ja: "Question JA: レポートの形式は何ですか？",
        },
      },
      {
        q: {
          en: "Is the service for individuals or businesses?",
          ja: "個人向けですか？法人向けですか？",
        },
        a: {
          en: "Both. Individual drivers, corporate fleets, dealership programs and government / public-sector users all subscribe to the service, with scheduled email reports tuned for each use case.",
          ja: "Question JA: 個人向けですか？法人向けですか？",
        },
      },
      {
        q: {
          en: "Are reports delivered in Japanese or English?",
          ja: "レポートは日本語ですか、英語ですか？",
        },
        a: {
          en: "Both. Customers choose the email language (Japanese or English) at signup, and the PDF and CSV layouts are tuned for the chosen language without changing the underlying data.",
          ja: "両方に対応しています。お申込時に配信メールの言語（日本語または英語）をお選びいただけます。データそのものは共通で、PDF・CSVの体裁が選択言語に合わせて調整されます。",
        },
      },
    ],
  },

  japanchangemoney: {
    accent: "#0E5FB0",
    accentSoft: "#E8F0FA",
    accentDeep: "#084785",
    domain: "JapanChangeMoney.com",
    href: "https://japanchangemoney.com",
    established: "2027 (planned launch)",
    customers: "Established customer base",
    eyebrow: { ja: "運営サービス / 03 — 準備中", en: "Operated Service / 03 — Planned" },
    title: { ja: "JapanChangeMoney.com", en: "JapanChangeMoney.com" },
    headline: {
      ja: "透明性のあるレートで、日本国内の通貨両替をもっと分かりやすく。",
      en: "Clearer currency exchange across Japan — built on transparent rates.",
    },
    intro: {
      ja: "JapanChangeMoney.comは、JapanETCcard.comで築いてきたお客様基盤を活かして開発中の外貨両替関連サービスです。日本本土と沖縄の利用者を対象に、米ドル（USD）と日本円（JPY）の両替のみを、リアルタイムレートと分かりやすい利用者向けガイドとともに提供する予定です。現時点では準備中であり、実際の両替取引は行っていません。2027年の公開を目標としています。",
      en: "JapanChangeMoney.com is a planned currency exchange service in development, building on the established customer base we have supported through JapanETCcard.com. Designed for residents across mainland Japan and Okinawa, it plans to support exchange between US dollars (USD) and Japanese yen (JPY) only, with real-time rates and clear customer education. The service is in beta development today and is not yet conducting any transactions. Targeted launch: 2027.",
    },
    stats: [
      { value: "2027", labelJa: "公開予定", labelEn: "Planned launch" },
      { valueJa: "8言語", valueEn: "8 languages", labelJa: "対応予定言語", labelEn: "Planned languages" },
      { value: "USD ↔ JPY", labelJa: "予定対応通貨", labelEn: "Planned currencies" },
      { value: "KYC / AML", labelJa: "本人確認に配慮", labelEn: "Identity-verification aware" },
    ],
    features: [
      {
        titleJa: "ライブ為替レート",
        titleEn: "Live exchange rates",
        bodyJa: "米ドル（USD）と日本円（JPY）のリアルタイムレートを表示する予定です。",
        bodyEn: "Real-time rates for US dollars (USD) against Japanese yen (JPY) will be displayed.",
      },
      {
        titleJa: "本人確認に配慮した設計",
        titleEn: "Identity-verification aware design",
        bodyJa: "KYC/AML（本人確認・反マネーロンダリング）に配慮した利用フロー、リスクベースの確認、QRコード/セルフィー確認を計画しています。",
        bodyEn: "KYC/AML-aware workflows, risk-based verification levels, plus QR-code and selfie checks are planned for safer operations.",
      },
      {
        titleJa: "両替所ディレクトリ",
        titleEn: "Exchange directory",
        bodyJa: "東京・神奈川など主要エリアの両替所情報を、住所・地図・営業時間とともに掲載する予定です。",
        bodyEn: "Directory listings of exchange locations across Tokyo, Kanagawa and other regions — with addresses, maps and hours.",
      },
      {
        titleJa: "ロイヤリティ・ETC優遇プログラム",
        titleEn: "Loyalty & ETC advantage",
        bodyJa: "生涯ティア制のロイヤリティと、ETCカードご利用者様向けの特別ティアを予定しています。",
        bodyEn: "A lifetime loyalty tier system with a dedicated ETC Advantage tier for existing JapanETCcard customers is planned.",
      },
      {
        titleJa: "教育コンテンツ",
        titleEn: "Customer education",
        bodyJa: "外貨購入時の注意点、USD/JPYの見通し、金融政策の解説、偽札への注意など、利用者が安心して判断できる情報を整備します。",
        bodyEn: "Currency buying tips, USD/JPY outlooks, monetary-policy explainers and counterfeit-bill guidance — to help customers exchange more confidently.",
      },
      {
        titleJa: "ライブチャート・計算ツール",
        titleEn: "Live charts & calculators",
        bodyJa: "為替レート計算、ローン計算、市場ステータスボードなど、実務に役立つツール群の搭載を予定しています。",
        bodyEn: "Rate calculators, loan calculators and a market status board are planned to support practical decisions.",
      },
    ],
    audiences: [
      { ja: "米軍・SOFA関係者", en: "U.S. military & SOFA families" },
      { ja: "退役・退職後も日本在住の方", en: "Retired personnel in Japan" },
      { ja: "外国人居住者", en: "Foreign residents" },
      { ja: "ETCサービス既存ユーザー", en: "Existing ETC service users" },
      { ja: "本土・沖縄の在住者", en: "Mainland Japan & Okinawa" },
      { ja: "出張・観光で日本円が必要な方", en: "Business travelers needing JPY" },
    ],
    note: {
      ja: "JapanChangeMoney.comは現在ベータ開発中であり、両替取引は行っておりません。本ページの記載は将来の予定であり、サービス公開、登録、規制上の位置付けを意味するものではありません。",
      en: "JapanChangeMoney.com is currently in beta development and is not conducting money exchange transactions. The descriptions on this page are forward-looking and do not imply launch, licensing, or regulated status until officially announced.",
    },
    support: {
      email: "support@japanchangemoney.com",
      hoursJa: "現在準備中 — NoJo Enterpriseのお問い合わせ窓口にてご対応いたします。",
      hoursEn: "Service is in development — general inquiries handled by the NoJo Enterprise contact desk.",
    },
    faqs: [
      {
        q: {
          en: "Is JapanChangeMoney live?",
          ja: "JapanChangeMoneyはすでに利用できますか？",
        },
        a: {
          en: "No. JapanChangeMoney is currently in beta development. We are not conducting any exchange transactions today. The descriptions on this page are forward-looking and do not imply launch or any regulated status.",
          ja: "Question JA: JapanChangeMoneyはすでに利用できますか？",
        },
      },
      {
        q: {
          en: "When is JapanChangeMoney planned to launch?",
          ja: "公開予定はいつですか？",
        },
        a: {
          en: "The current target is 2027. The Roadmap page (https://www.nojoenterprise.com/en/roadmap) carries the public quarterly milestones — please check there for the latest progress.",
          ja: "Question JA: 公開予定はいつですか？",
        },
      },
      {
        q: {
          en: "What does JapanChangeMoney aim to do?",
          ja: "JapanChangeMoneyは何を目指していますか？",
        },
        a: {
          en: "JapanChangeMoney is planned as a transparent currency-exchange service for residents and visitors in Japan, designed around clear pricing, multilingual support, and the same operational discipline NoJo Enterprise applies to JapanETCcard and JapanTollReceipts.",
          ja: "Question JA: JapanChangeMoneyは何を目指していますか？",
        },
      },
      {
        q: {
          en: "How can I be notified at launch?",
          ja: "公開時の連絡を受け取るにはどうすればいいですか？",
        },
        a: {
          en: "Please send a note through the NoJo Enterprise Contact page or follow the public roadmap. Until the service is officially announced, there is no sign-up or pre-registration step.",
          ja: "Question JA: 公開時の連絡を受け取るにはどうすればいいですか？",
        },
      },
    ],
  },
};

/**
 * SERVICE_NARRATIVE — additive corporate narrative blocks for each service
 * detail page (added 2026-02). Keeps the founder/origin story and the
 * "Part of NoJo Enterprise" connector separate from SERVICE_CONTENT so
 * existing fields stay untouched.
 *
 * Pillar mapping:
 *   - mobility           = purple #5B2E83
 *   - records            = green  #2F8F3C
 *   - digitalInfra       = blue   #0E5FB0
 */
export const SERVICE_NARRATIVE = {
  japanetccard: {
    whyBuilt: {
      eyebrowJa: "立ち上げの理由",
      eyebrowEn: "Origin",
      titleJa: "このサービスを立ち上げた理由",
      titleEn: "Why we built this",
      bodyJa:
        "2011年、日本で英語対応のETCサービスを実現するために、銀行・金融機関・NEXCO・行政の窓口を回り続けました。2年間で12回「無理だ」と言われ、13回目でようやく「Yes」と返ってきたところから、すべてが始まりました。当時、日本に住む外国人居住者や米軍関係者、大使館関係者には、日本語が分からないという理由だけで使いにくい仕組みが多くありました。JapanETCcard.comは、その不便を「日本のシステムに合わせながら、英語でも安心して使える形」にして提供するために、現場の課題そのものから生まれたサービスです。",
      bodyEn:
        "In 2011 we began knocking on doors — banks, NEXCO, government channels — trying to build an English-language ETC service for Japan. After two full years and twelve straight \"no\"s, the thirteenth attempt finally became a \"yes\". JapanETCcard.com exists because foreign residents, U.S. military families and embassy staff in Japan kept hitting the same wall: a system that only worked smoothly in Japanese. We built this service to work the way Japan's ETC system already works — but in a form people can actually access in English, with real bilingual support behind it.",
      pillarsJa: ["お客様課題を出発点に", "現場で確認された不便", "13年にわたる運用実績"],
      pillarsEn: ["Started from real customer friction", "Built around observed pain points", "13 years of continuous operation"],
    },
    partOfNojo: {
      pillar: "mobility",
      pillarLabelJa: "モビリティ基盤",
      pillarLabelEn: "Mobility foundation",
      bodyJa:
        "JapanETCcardは、NoJo Enterpriseのモビリティ基盤を担うサービスです。日本国内のドライバーが、ETC・高速道路を英語サポート付きで安心して利用できるよう、契約・お支払い・実用的なアカウント管理までを一貫してご提供します。",
      bodyEn:
        "JapanETCcard represents NoJo Enterprise's mobility foundation, helping drivers in Japan access ETC toll roads with English-language support, customer care, and practical account management.",
    },
  },

  japantollreceipts: {
    whyBuilt: {
      eyebrowJa: "立ち上げの理由",
      eyebrowEn: "Origin",
      titleJa: "このサービスを立ち上げた理由",
      titleEn: "Why we built this",
      bodyJa:
        "JapanETCcardを運営する中で、ETC利用明細・利用証明書の確認作業がお客様の大きな負担になっていることが見えてきました。MEISAI（ETC利用照会サービス）には公式のデータがあるものの、日常業務に取り込みやすい形にはなっていません。手作業でのコピー＆ペースト、紙での整理、月末の経費処理 ─ どの場面でも時間と神経が消費されていました。JapanTollReceipts.jpは、その「ETC利用記録を実務で使える形に整えるレイヤー」を埋めるために生まれました。13年分のETCサポート経験を、記録・レポート・経費処理の負担軽減に変えていく仕組みです。",
      bodyEn:
        "Operating JapanETCcard for over a decade made one customer pain point unmistakable: turning raw ETC toll data into usable records was eating real hours every month. Japan's MEISAI system already publishes the underlying data — but not in a form that day-to-day business teams, fleet managers, embassies or accounting departments can actually work with. JapanTollReceipts.jp exists to fill that gap. We built it to take everything we learned supporting 13 years of ETC customers and turn it into a quiet, dependable layer that produces clean toll receipts, organised reports, and bilingual exports — so the work shrinks from hours to minutes.",
      pillarsJa: ["13年分のETCサポート経験", "現場で観察された反復作業", "実務で使える記録形式へ"],
      pillarsEn: ["13 years of ETC support knowledge", "Observed repetition in customer workflows", "Built for real operational use"],
    },
    partOfNojo: {
      pillar: "records",
      pillarLabelJa: "記録・レポート基盤",
      pillarLabelEn: "Records & reporting layer",
      bodyJa:
        "JapanTollReceiptsは、NoJoのモビリティ運営で培った知見を「記録・レポート・デジタル基盤」の領域へと広げるサービスです。ETC利用データを、お客様・事業者・経理担当の方が日常業務でそのまま使える形式に整えることを目的としています。",
      bodyEn:
        "JapanTollReceipts extends NoJo's mobility experience into records, reporting and digital infrastructure by helping customers organise ETC usage into clear, usable receipt data.",
    },
  },

  japanchangemoney: {
    whyBuilt: {
      eyebrowJa: "立ち上げの理由",
      eyebrowEn: "Origin",
      titleJa: "このサービスを立ち上げた理由",
      titleEn: "Why we built this",
      bodyJa:
        "日本での通貨両替は、相場の確認が分かりづらく、提示レートと実勢レートの差や手数料の構造がお客様に伝わりにくい場面が多くあります。JapanETCcardやJapanTollReceiptsの運営を通じて、日本で暮らす外国人居住者・出張者・短期滞在の方々から「相場と費用が見えにくい」というご相談を継続的に受けてきました。JapanChangeMoneyは、その課題を出発点に、両替に関する分かりやすい情報・レートの透明性・利用シーンごとのガイドを、日本語・英語の両方で提供することを目指して準備を進めています。",
      bodyEn:
        "Operating JapanETCcard and JapanTollReceipts has put us in front of foreign residents, business visitors, and short-stay travellers for over a decade — and one pattern keeps repeating: it's hard to see what a fair exchange rate looks like in Japan, and even harder to understand how fees are structured. JapanChangeMoney is being built around that gap. The goal is plain-language exchange information, rate transparency, and situational guidance — bilingual EN/JA from day one. The service is currently in beta development.",
      pillarsJa: ["長年お客様からいただいた声", "レートと費用の透明性", "日本語・英語の両対応"],
      pillarsEn: ["Years of consistent customer feedback", "Rate and fee transparency", "Bilingual EN / JA from day one"],
    },
    partOfNojo: {
      pillar: "digitalInfra",
      pillarLabelJa: "金融サービス領域への拡張",
      pillarLabelEn: "Financial-service expansion",
      bodyJa:
        "JapanChangeMoneyは、NoJo Enterpriseの金融サービス領域への拡張を担うサービスです。日本国内で暮らす・訪れるお客様に向けて、実用的な両替情報、レートの透明性、そしてカスタマーサポートを軸に準備を進めています。",
      bodyEn:
        "JapanChangeMoney represents NoJo's financial-service expansion, focused on practical currency exchange information, rate transparency, and customer support for people living in or visiting Japan.",
    },
  },
};
